import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MessageSquare, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "../components/ui/sonner";
import { MessageThread } from "../components/MessageThread";
import { MatchSuggestions } from "../components/MatchSuggestions";
import { FeePaymentPanel } from "../components/FeePaymentPanel";
import { ReportConcernModal } from "../components/ReportConcernModal";
import { SessionToolkit } from "../components/SessionToolkit";
import { formatDate } from "../lib/dateUtils";

const GALLERY_STATUS_LABELS = { pending: "Pending Gallery", gallery_added: "Gallery Added", delivered: "Delivered", expired: "Expired" };

const CONSENT_LABELS = { hand_holding: "Hand holding", hugging: "Hugging", sitting_close: "Sitting close", arm_around_shoulder: "Arm around shoulder", other: "Other" };

const formatConsent = (consent) => {
  const active = Object.keys(CONSENT_LABELS).filter((k) => consent?.[k]);
  return active.length ? active.map((k) => CONSENT_LABELS[k]).join(", ") : "No physical contact";
};

export default function SessionManage() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [galleryUrl, setGalleryUrl] = useState("");
  const [galleryPassword, setGalleryPassword] = useState("");
  const [galleryExpiration, setGalleryExpiration] = useState("");
  const [galleryDelivery, setGalleryDelivery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [openThread, setOpenThread] = useState(null);
  const [reportBookingId, setReportBookingId] = useState(null);

  const load = () => {
    api.get(`/sessions/${id}`).then(({ data }) => {
      setSession(data);
      setGalleryUrl(data.gallery_url || "");
      setGalleryPassword(data.gallery_password || "");
      setGalleryExpiration(data.gallery_expiration_date || "");
      setGalleryDelivery(data.gallery_delivery_date || "");
    });
    api.get(`/sessions/${id}/registrations`).then(({ data }) => setRegistrations(data));
    api.get(`/sessions/${id}/matches`).then(({ data }) => setMatches(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleSelect = (bookingId) => {
    setSelected((s) => (s.includes(bookingId) ? s.filter((x) => x !== bookingId) : s.length < 2 ? [...s, bookingId] : s));
  };

  const createMatch = async (bookingIdA, bookingIdB) => {
    await api.post(`/sessions/${id}/matches`, null, { params: { booking_id_a: bookingIdA, booking_id_b: bookingIdB } });
    toast.success("Match created!");
    setSelected([]);
    load();
  };

  const createSelectedMatch = async () => {
    if (selected.length !== 2) return toast.error("Select exactly two Strangers to match.");
    try {
      await createMatch(selected[0], selected[1]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveGalleryUrl = async () => {
    await api.put(`/sessions/${id}/gallery`, null, {
      params: {
        gallery_url: galleryUrl,
        gallery_password: galleryPassword || undefined,
        gallery_expiration_date: galleryExpiration || undefined,
        gallery_delivery_date: galleryDelivery || undefined,
      },
    });
    toast.success("Gallery details saved");
    load();
  };

  const markGalleryReady = async () => {
    await api.put(`/sessions/${id}/gallery`, null, { params: { gallery_ready: true } });
    toast.success("Strangers can now view their story");
    load();
  };

  const markCompleted = async () => {
    await api.put(`/sessions/${id}/status`, null, { params: { status: "completed" } });
    toast.success("Session marked completed");
    load();
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    try {
      await api.post(`/sessions/${id}/gallery/photos`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photos uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const matchedBookingIds = new Set(matches.flatMap((m) => m.booking_ids));

  if (!session) return <div className="py-32 text-center font-medium">Loading session...</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="session-manage-page">
      <h1 className="font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-2">{session.title}</h1>
      <p className="text-midnight-black/60 font-medium mb-10">
        {session.city}, {session.state} · {formatDate(session.session_date)} · Status: {session.status}
      </p>

      <SessionToolkit sessionId={id} sessionType={session.session_type} matches={matches} registrations={registrations} />

      <div className="mb-14">
        <MatchSuggestions sessionId={id} onCreateMatch={createMatch} />
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-extrabold uppercase text-lg">Registrations ({registrations.length})</h2>
          <Button data-testid="create-match-button" onClick={createSelectedMatch} disabled={selected.length !== 2} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
            MATCH SELECTED ({selected.length}/2)
          </Button>
        </div>
        {registrations.length === 0 ? (
          <p className="text-sm font-medium text-midnight-black/60" data-testid="no-registrations">
            No one has registered for this session yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {registrations.map((r) => (
              <div
                key={r.id}
                data-testid={`registration-${r.id}`}
                className={`border-2 rounded-xl p-4 transition-colors ${selected.includes(r.id) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"} ${r.status !== "registered" ? "opacity-90" : ""}`}
              >
                <div onClick={() => r.status === "registered" && !matchedBookingIds.has(r.id) && toggleSelect(r.id)} className="cursor-pointer flex items-start gap-3">
                  {r.stranger?.has_profile_photo && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile-photo/${r.stranger.id}`}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover border border-soft-gray shrink-0"
                      data-testid={`registration-photo-${r.id}`}
                    />
                  )}
                  <div>
                    <p className="font-extrabold text-sm uppercase">
                      {r.stranger?.first_name} {r.stranger?.last_name}
                    </p>
                    <p className="text-xs font-medium text-midnight-black/60 mt-1">
                      Preference: {r.stranger?.session_preference || "Not set"}
                    </p>
                    <p className="text-xs font-medium text-midnight-black/60" data-testid={`registration-consent-${r.id}`}>
                      Consents to: {formatConsent(r.stranger?.consent)}
                    </p>
                    <p className="text-xs font-medium text-midnight-black/60">Status: {r.status}</p>
                  </div>
                </div>
                <button
                  data-testid={`toggle-message-thread-${r.id}`}
                  onClick={() => setOpenThread(openThread === r.id ? null : r.id)}
                  className="mt-3 flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-stranger-orange hover:underline"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> {openThread === r.id ? "HIDE MESSAGES" : "MESSAGE"}
                </button>
                {openThread === r.id && (
                  <div className="mt-4 border-t border-soft-gray pt-4">
                    <MessageThread bookingId={r.id} />
                  </div>
                )}
                {r.financially_cleared && (
                  <FeePaymentPanel bookingId={r.id} partyType="photographer" bookingNumber={r.booking_number} wrapperClassName="mt-4" />
                )}
                <button
                  data-testid={`report-concern-${r.id}`}
                  onClick={() => setReportBookingId(r.id)}
                  className="mt-3 flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-midnight-black/40 hover:text-red-500"
                >
                  <ShieldAlert className="w-3.5 h-3.5" /> REPORT A CONCERN
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-14">
        <h2 className="font-extrabold uppercase text-lg mb-4">Matches ({matches.length})</h2>
        {matches.length === 0 ? (
          <p className="text-sm font-medium text-midnight-black/60" data-testid="no-matches">
            No matches created yet.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="matches-list">
            {matches.map((m) => (
              <li key={m.id} className="text-sm font-medium text-midnight-black/70">
                Match {m.id.slice(-6)} — 2 Strangers paired
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mb-14 border border-soft-gray rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-extrabold uppercase text-lg">Gallery</h2>
          {session.gallery_status && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-warm-white border border-soft-gray" data-testid="gallery-status-badge">
              {GALLERY_STATUS_LABELS[session.gallery_status] || session.gallery_status}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Input data-testid="gallery-url-input" placeholder="Paste external gallery link (Pixieset, Google Drive...)" value={galleryUrl} onChange={(e) => setGalleryUrl(e.target.value)} className="sm:col-span-2" />
          <Input data-testid="gallery-password-input" placeholder="Gallery password (if applicable)" value={galleryPassword} onChange={(e) => setGalleryPassword(e.target.value)} />
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Delivery Date</label>
            <Input data-testid="gallery-delivery-date-input" type="date" value={galleryDelivery} onChange={(e) => setGalleryDelivery(e.target.value)} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Expiration Date</label>
            <Input data-testid="gallery-expiration-date-input" type="date" value={galleryExpiration} onChange={(e) => setGalleryExpiration(e.target.value)} />
          </div>
          <Button data-testid="save-gallery-url-button" onClick={saveGalleryUrl} className="bg-midnight-black text-clean-white font-bold rounded-full self-end">
            SAVE GALLERY DETAILS
          </Button>
        </div>
        <div className="mb-4">
          <label className="text-sm font-bold block mb-2">Or upload photos directly</label>
          <input type="file" data-testid="gallery-upload-input" multiple accept="image/*" onChange={uploadPhotos} disabled={uploading} />
          {session.gallery_photos?.length > 0 && <p className="text-xs font-medium text-midnight-black/60 mt-2">{session.gallery_photos.length} photo(s) uploaded</p>}
        </div>
        <div className="flex gap-3">
          <Button data-testid="mark-gallery-ready-button" onClick={markGalleryReady} disabled={session.gallery_ready} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
            {session.gallery_ready ? "GALLERY IS LIVE" : "MARK GALLERY READY"}
          </Button>
          <Button data-testid="mark-completed-button" onClick={markCompleted} disabled={session.status === "completed"} variant="outline" className="rounded-full font-bold">
            {session.status === "completed" ? "SESSION COMPLETED" : "MARK SESSION COMPLETED"}
          </Button>
        </div>
      </div>

      {reportBookingId && <ReportConcernModal bookingId={reportBookingId} onClose={() => setReportBookingId(null)} />}
    </div>
  );
}
